import { Button } from "@/src/components/buttons";
import { Card, CompartmentCard, ReferenceCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import { Patient, RefAnswer, Reference } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  getUserId,
  getUserShortName,
  jsonAbstract,
  uid,
  updateOrPush,
} from "@/src/utils/utils";
import { useState } from "react";
import { ReferenceForm } from "../admin/referentiel/referenceForm";
import { FieldAnswersList } from "../fields/fieldsAnswersList";
import { RefAnswerForm } from "../fields/referenceFieldsForm";
import { openSnomedConceptPicker } from "../snomed/SnomedConceptPicker";

function DiagnosticsHistory({
  patient,
  current,
  onChange,
}: {
  patient: Patient;
  current: (RefAnswer | null | undefined)[];
  onChange: (newValue: (RefAnswer | null | undefined)[]) => void;
}) {
  let [refAnswers, setRefAnswers] = useState<(RefAnswer | null | undefined)[]>(
    current || []
  );
  let diagnostics = refAnswers;

  function addToRefAnswers(refAnswer: RefAnswer) {
    refAnswers = updateOrPush(
      refAnswers,
      refAnswer,
      (item) => item?.reference?.id === refAnswer.reference?.id
    );

    setRefAnswers([...refAnswers]);
    onChange(refAnswers);
  }

  function removeFromRefAnswers(refAnswer: RefAnswer | null | undefined) {
    refAnswers ||= [];
    refAnswers = refAnswers.filter(
      (item) => item?.reference?.id !== refAnswer?.reference?.id
    );
    setRefAnswers([...refAnswers]);
    onChange(refAnswers);
  }

  return (
    <CompartmentCard
      title="Diagnostic(s)"
      onClick={async () => {
        await openSnomedConceptPicker({
          title: "Choisir un diagnostic",
          parentIds: ["disease", "finding"],
          category: "diagnostic",
          onSelect: async (reference) => {
            let refAnswer: RefAnswer = {
              patient,
              reference: reference,
              creator: getUserId(),
              fieldAnswers: [],
            };

            await openDrawer({
              id: "referenceFieldsDrawer",
              title: `Ajouter ${reference.name}`,
              content: (
                <RefAnswerForm
                  patient={patient}
                  refAnswer={refAnswer}
                  onChange={(newPatientsReference) => {
                    refAnswer = newPatientsReference;
                  }}
                ></RefAnswerForm>
              ),
              onConfirm: async () => {
                addToRefAnswers(refAnswer);

                closeDrawer("diagnosticsListDrawer");
              },
            });
          },
        });

        // openDrawer({
        //   id: "diagnosticsListDrawer",
        //   title: "Choisir un diagnostic",
        //   additionalClasses: ["medium"],
        //   content: (
        //     <DiagnosticsList
        //       onSelect={async (reference) => {
        //         let refAnswer: RefAnswer = {
        //           patient,
        //           reference: reference,
        //           creator: getUserId(),
        //           fieldAnswers: [],

        //         };

        //         await openDrawer({
        //           id: "referenceFieldsDrawer",
        //           title: `Ajouter ${reference.name}`,
        //           content: (
        //             <RefAnswerForm
        //               patient={patient}
        //               refAnswer={refAnswer}
        //               onChange={(newPatientsReference) => {
        //                 refAnswer = newPatientsReference;
        //               }}
        //             ></RefAnswerForm>
        //           ),
        //           onConfirm: async () => {
        //             addToRefAnswers(refAnswer);

        //             closeDrawer("diagnosticsListDrawer");
        //           },
        //         });
        //       }}
        //     ></DiagnosticsList>
        //   ),
        // });
      }}
    >
      <ListConstructor
        data={diagnostics}
        isLoading={false}
        isError={false}
        error={null}
        emptyChild={
          <div className="gray center p10">Aucun diagnostic ajouté</div>
        }
      >
        <div className="c1 gap10">
          {diagnostics?.map((refAnswer, index) => {
            return (
              <Card
                key={uid()}
                color="blue"
                animationIndex={index}
                title={
                  <div className="capitalize">{refAnswer?.reference?.name}</div>
                }
                subTitle={getUserShortName(refAnswer?.creator)}
                leading={<></>}
                onClick={async () => {
                  let newRefAnswer: RefAnswer = {
                    ...jsonAbstract(refAnswer),
                  };
                  openDrawer({
                    id: "referenceFieldsDrawer",
                    title: `Modifier ${newRefAnswer.reference?.name}`,
                    content: (
                      <RefAnswerForm
                        patient={patient}
                        refAnswer={newRefAnswer}
                        onChange={(newPatientsReference) => {
                          newRefAnswer = newPatientsReference;
                        }}
                      ></RefAnswerForm>
                    ),
                    onConfirm: async () => {
                      addToRefAnswers(newRefAnswer);
                    },
                  });
                }}
                onDelete={() => {
                  removeFromRefAnswers(refAnswer);
                }}
              >
                <div className="c1 gap10 mtop10">
                  <FieldAnswersList
                    patient={patient}
                    reference={refAnswer?.reference}
                    answers={refAnswer?.fieldAnswers || []}
                  ></FieldAnswersList>
                </div>
                <div className="gray"> {refAnswer?.notes} </div>
              </Card>
            );
          })}
        </div>
      </ListConstructor>
    </CompartmentCard>
  );
}

function DiagnosticsList({
  onSelect,
}: {
  onSelect: (reference: Reference) => void;
}) {
  let [searchKey, setSearchKey] = useState("");
  const [reload, setReload] = useState(0);

  return (
    <div className="grid c1 gap10">
      <FetchableList
        queryKey={["DiagnosticsList", searchKey, reload]}
        queryFn={() =>
          apiFetch(
            "references",
            "loadMany",
            {
              userId: getUserId(),

              categoriesIds: ["diagnostic"],
              searchKey,
            },
            "Erreur lors du chargement des diagnostics"
          )
        }
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        emptyChild={
          <div className="grid c1 gap10">
            <div className="gray p20 center">
              Aucun diagnostic trouvé
              <strong>{searchKey && `content ${searchKey}`}</strong>
            </div>
            {searchKey && (
              <Button
                onClick={async () => {
                  let reference: Reference = {
                    name: searchKey,
                    category: "diagnostic",
                    creator: getUserId(),
                  };

                  await openDrawer({
                    id: "diagnosticFormDrawer",
                    title: "Créer Diagnostic",
                    content: (
                      <ReferenceForm
                        current={reference}
                        categories={["diagnostic"]}
                        onChange={(newValue) => {
                          reference = newValue;
                        }}
                      ></ReferenceForm>
                    ),
                    onConfirm: async () => {
                      let result = await apiFetch(
                        "references",
                        "addOne",
                        {
                          reference,
                        },
                        "Erreur lors de l'ajout du diagnostic"
                      );
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
              >
                Créer le diagnostic {searchKey}
              </Button>
            )}
          </div>
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((reference) => {
              return (
                <ReferenceCard
                  key={uid()}
                  searchKey={searchKey}
                  reference={reference}
                  onClick={() => {
                    onSelect(reference);
                  }}
                />
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

export { DiagnosticsHistory };
